import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { hexbin } from 'd3-hexbin';
import { getPerformanceColor } from './mockData';

/**
 * Custom hook to create and update a D3.js scatter plot
 * 
 * @param {Array} data - Array of deputy objects with performance metrics
 * @param {Object} options - Configuration options for the chart
 * @param {Function} onPointHover - Callback when hovering over a point
 * @param {Function} onPointLeave - Callback when leaving a point
 * @returns {Object} - Ref to attach to the SVG element
 */
const useD3ScatterPlot = (data, options = {}, onPointHover, onPointLeave) => {
  const svgRef = useRef(null);
  
  // Default options
  const {
    width = 1200,
    height = 800,
    margin = { top: 100, right: 50, bottom: 100, left: 100 },
    backgroundColor = '#6366f1', // Indigo background color
    gridColor = 'rgba(255, 255, 255, 0.2)',
    textColor = '#ffffff',
    pointRadius = 4,
    hexbinRadius = 12,
    hexbinOpacity = 0.5,
    duration = 750,
    showPartyLabels = true,
    highlightedParty = null
  } = options;

  useEffect(() => {
    if (!data || !svgRef.current) return;

    // Select the SVG element
    const svg = d3.select(svgRef.current);
    
    // Clear existing content
    svg.selectAll('*').remove();
    
    // Set SVG dimensions
    svg
      .attr('width', width)
      .attr('height', height)
      .style('background-color', backgroundColor);
    
    // Create scales
    const xScale = d3.scaleLinear()
      .domain([0, 1])
      .range([margin.left, width - margin.right]);
    
    const yScale = d3.scaleLinear()
      .domain([-100, 100])
      .range([height - margin.bottom, margin.top]);
    
    // Create a group for the chart content
    const g = svg.append('g');
    
    // Create a group for hexbins (below points)
    const hexbinGroup = g.append('g').attr('class', 'hexbin-group');
    
    // Create a group for points (above hexbins)
    const pointsGroup = g.append('g').attr('class', 'points-group');
    
    // Add grid lines
    // Vertical grid lines
    g.selectAll('.grid-line-vertical')
      .data(d3.range(0, 1.1, 0.1))
      .enter()
      .append('line')
      .attr('class', 'grid-line-vertical')
      .attr('x1', d => xScale(d))
      .attr('y1', margin.top)
      .attr('x2', d => xScale(d))
      .attr('y2', height - margin.bottom)
      .attr('stroke', gridColor)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');
    
    // Horizontal grid lines
    g.selectAll('.grid-line-horizontal')
      .data(d3.range(-100, 101, 20))
      .enter()
      .append('line')
      .attr('class', 'grid-line-horizontal')
      .attr('x1', margin.left)
      .attr('y1', d => yScale(d))
      .attr('x2', width - margin.right)
      .attr('y2', d => yScale(d))
      .attr('stroke', gridColor)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');
    
    // Add axis labels
    // X-axis labels
    g.append('text')
      .attr('class', 'axis-label-left')
      .attr('x', margin.left)
      .attr('y', height - margin.bottom + 40)
      .attr('text-anchor', 'middle')
      .attr('fill', textColor)
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .text('MENOR MÉDIA');
    
    g.append('text')
      .attr('class', 'axis-label-right')
      .attr('x', width - margin.right)
      .attr('y', height - margin.bottom + 40)
      .attr('text-anchor', 'middle')
      .attr('fill', textColor)
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .text('MAIOR MÉDIA');
    
    // Y-axis labels
    g.append('text')
      .attr('class', 'axis-label-top')
      .attr('x', -margin.top)
      .attr('y', margin.left - 60)
      .attr('transform', 'rotate(-90)')
      .attr('text-anchor', 'middle')
      .attr('fill', textColor)
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .text('MELHOR DESEMPENHO');
    
    g.append('text')
      .attr('class', 'axis-label-bottom')
      .attr('x', -(height - margin.bottom))
      .attr('y', margin.left - 60)
      .attr('transform', 'rotate(-90)')
      .attr('text-anchor', 'middle')
      .attr('fill', textColor)
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .text('PIOR DESEMPENHO');
    
    // Add Y-axis values
    g.selectAll('.y-axis-value')
      .data([-100, -80, -60, -40, -20, 0, 20, 40, 60, 80, 100])
      .enter()
      .append('text')
      .attr('class', 'y-axis-value')
      .attr('x', width - margin.right + 20)
      .attr('y', d => yScale(d))
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')
      .attr('fill', textColor)
      .style('font-size', '12px')
      .text(d => (d > 0 ? '+' : '') + d);
    
    // Add "SEM PROPOSTAS" label at y=0
    g.append('text')
      .attr('class', 'sem-propostas-label')
      .attr('x', margin.left - 80)
      .attr('y', yScale(0))
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')
      .attr('fill', textColor)
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('SEM');
    
    g.append('text')
      .attr('class', 'sem-propostas-label')
      .attr('x', margin.left - 80)
      .attr('y', yScale(0) + 20)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')
      .attr('fill', textColor)
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('PROPOSTAS');
    
    // Create hexbin generator
    const hexbinGenerator = hexbin()
      .x(d => xScale(d.media))
      .y(d => yScale(d.desempenho))
      .radius(hexbinRadius)
      .extent([[margin.left, margin.top], [width - margin.right, height - margin.bottom]]);
    
    // Group data by party for party labels
    const partiesByPosition = {};
    data.forEach(d => {
      const party = d.partido;
      if (!partiesByPosition[party]) {
        partiesByPosition[party] = {
          x: xScale(d.media),
          y: yScale(d.desempenho),
          count: 1
        };
      } else {
        partiesByPosition[party].x += xScale(d.media);
        partiesByPosition[party].y += yScale(d.desempenho);
        partiesByPosition[party].count += 1;
      }
    });
    
    // Calculate average position for each party
    Object.keys(partiesByPosition).forEach(party => {
      partiesByPosition[party].x /= partiesByPosition[party].count;
      partiesByPosition[party].y /= partiesByPosition[party].count;
    });
    
    // Add party labels horizontally across the top
    if (showPartyLabels) {
      const uniqueParties = [...new Set(data.map(d => d.partido))].sort();
      const partyLabelWidth = (width - margin.left - margin.right) / uniqueParties.length;
      
      // Add vertical lines for each party
      g.selectAll('.party-line')
        .data(uniqueParties)
        .enter()
        .append('line')
        .attr('class', 'party-line')
        .attr('x1', (d, i) => margin.left + (i * partyLabelWidth) + (partyLabelWidth / 2))
        .attr('y1', margin.top - 20)
        .attr('x2', (d, i) => margin.left + (i * partyLabelWidth) + (partyLabelWidth / 2))
        .attr('y2', height - margin.bottom)
        .attr('stroke', gridColor)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3');
      
      // Add party labels
      g.selectAll('.party-label')
        .data(uniqueParties)
        .enter()
        .append('text')
        .attr('class', 'party-label')
        .attr('x', (d, i) => margin.left + (i * partyLabelWidth) + (partyLabelWidth / 2))
        .attr('y', margin.top - 30)
        .attr('text-anchor', 'middle')
        .attr('fill', d => d === highlightedParty ? '#ffff00' : textColor)
        .style('font-size', '12px')
        .style('font-weight', d => d === highlightedParty ? 'bold' : 'normal')
        .text(d => d);
    }
    
    // Add individual points
    const points = pointsGroup.selectAll('.deputy-point')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'deputy-point')
      .attr('cx', d => xScale(d.media))
      .attr('cy', d => yScale(d.desempenho))
      .attr('r', pointRadius)
      .attr('fill', d => getPerformanceColor(d.desempenho))
      .attr('stroke', d => d.partido === highlightedParty ? '#ffff00' : 'none')
      .attr('stroke-width', d => d.partido === highlightedParty ? 2 : 0)
      .style('cursor', 'pointer')
      .style('opacity', 0) // Start with opacity 0 for animation
      .on('mouseover', function(event, d) {
        // Bring this point to the front
        this.parentNode.appendChild(this);
        
        d3.select(this)
          .attr('r', pointRadius * 1.5)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2)
          .style('opacity', 1);
        
        if (onPointHover) {
          onPointHover(d, {
            x: event.pageX,
            y: event.pageY
          });
        }
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .attr('r', pointRadius)
          .attr('stroke', d.partido === highlightedParty ? '#ffff00' : 'none')
          .attr('stroke-width', d.partido === highlightedParty ? 2 : 0);
        
        if (onPointLeave) {
          onPointLeave();
        }
      });
    
    // Animate points
    points.transition()
      .duration(duration)
      .delay((d, i) => i * 5)
      .style('opacity', 0.8);
    
    // Create hexbins for density visualization
    const bins = hexbinGenerator(data);
    
    // Add hexbin areas
    hexbinGroup.selectAll('.hexbin')
      .data(bins)
      .enter()
      .append('path')
      .attr('class', 'hexbin')
      .attr('d', hexbinGenerator.hexagon())
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      .attr('fill', 'rgba(255, 255, 255, 0.2)')
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1)
      .style('opacity', 0) // Start with opacity 0 for animation
      .style('pointer-events', 'none'); // Disable pointer events so points can be clicked through hexbins
    
    // Animate hexbins
    hexbinGroup.selectAll('.hexbin')
      .transition()
      .duration(duration)
      .delay((d, i) => i * 20)
      .style('opacity', hexbinOpacity);
    
    // Make the chart responsive
    const resize = () => {
      const containerWidth = svgRef.current.parentElement.clientWidth;
      if (containerWidth > 0) {
        const newWidth = containerWidth;
        const newHeight = (newWidth * height) / width;
        
        svg.attr('width', newWidth)
           .attr('height', newHeight);
        
        // Update scales
        xScale.range([margin.left, newWidth - margin.right]);
        yScale.range([newHeight - margin.bottom, margin.top]);
        
        // Update grid lines
        g.selectAll('.grid-line-vertical')
          .attr('x1', d => xScale(d))
          .attr('x2', d => xScale(d))
          .attr('y2', newHeight - margin.bottom);
        
        g.selectAll('.grid-line-horizontal')
          .attr('x2', newWidth - margin.right)
          .attr('y1', d => yScale(d))
          .attr('y2', d => yScale(d));
        
        // Update axis labels
        g.select('.axis-label-right')
          .attr('x', newWidth - margin.right);
        
        g.select('.axis-label-bottom')
          .attr('x', -(newHeight - margin.bottom));
        
        // Update Y-axis values
        g.selectAll('.y-axis-value')
          .attr('x', newWidth - margin.right + 20)
          .attr('y', d => yScale(d));
        
        // Update points
        pointsGroup.selectAll('.deputy-point')
          .attr('cx', d => xScale(d.media))
          .attr('cy', d => yScale(d.desempenho));
        
        // Update hexbins
        hexbinGenerator.extent([[margin.left, margin.top], [newWidth - margin.right, newHeight - margin.bottom]]);
        const newBins = hexbinGenerator(data);
        
        hexbinGroup.selectAll('.hexbin')
          .data(newBins)
          .attr('transform', d => `translate(${d.x}, ${d.y})`);
        
        // Update party labels
        if (showPartyLabels) {
          const uniqueParties = [...new Set(data.map(d => d.partido))].sort();
          const partyLabelWidth = (newWidth - margin.left - margin.right) / uniqueParties.length;
          
          g.selectAll('.party-label')
            .attr('x', (d, i) => margin.left + (i * partyLabelWidth) + (partyLabelWidth / 2));
        }
      }
    };
    
    // Initial resize
    resize();
    
    // Add resize event listener
    window.addEventListener('resize', resize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [data, options, onPointHover, onPointLeave, width, height, margin, backgroundColor, gridColor, textColor, pointRadius, hexbinRadius, hexbinOpacity, duration, showPartyLabels, highlightedParty]);
  
  return svgRef;
};

export default useD3ScatterPlot;
